import { useMemo, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ComputerIcon,
  LogoutSquare01Icon,
  MoreHorizontalIcon,
  Moon02Icon,
  Search01Icon,
  Sun03Icon,
  Tick02Icon,
} from "@hugeicons/core-free-icons";
import { useAuth } from "@/auth/use-auth";
import { $api } from "@/lib/api-client";
import { useDisplayLanguage } from "@/lib/display-language";
import { type Theme, getTheme, setTheme } from "@/lib/theme";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/popover";
import { cn } from "@/lib/utils";

interface UserMenuProps {
  collapsed?: boolean;
}

function getInitials(name: string | null, email: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0];
    const last = parts.at(-1);
    if (parts.length >= 2 && first && last) {
      const firstChar = first[0];
      const lastChar = last[0];
      if (firstChar && lastChar) {
        return (firstChar + lastChar).toUpperCase();
      }
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "?";
}

/**
 * Combined user/account popover for the sidebar footer.
 *
 * Replaces the trio of standalone widgets (UserMenu / ThemeSwitcher /
 * LanguageSelector) with a single popup matching the design in
 * `voicebot-ui-redesign/project/src/shell.jsx` `UserCard`.
 *
 * Shows current user identity, a searchable language picker, a
 * light/dark/system theme toggle, and a sign-out action.
 */
export function UserMenu({ collapsed = false }: Readonly<UserMenuProps>) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [language, setLanguage] = useDisplayLanguage();
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => getTheme());

  const { data: languages } = $api.useQuery("get", "/api/config/languages");

  const filteredLangs = useMemo(() => {
    if (!languages) return [];
    if (!search) return languages;
    const q = search.toLowerCase();
    return languages.filter(
      (l) =>
        l.code.toLowerCase().includes(q) || l.name.toLowerCase().includes(q),
    );
  }, [languages, search]);

  if (!user) return null;

  const displayName = user.name || user.email || "User";
  const initials = getInitials(user.name, user.email);

  const handleThemeChange = (next: Theme) => {
    setTheme(next);
    setCurrentTheme(next);
  };

  const handleSelectLanguage = (code: string) => {
    setLanguage(code);
  };

  const handleSignOut = () => {
    setOpen(false);
    logout();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Open account menu"
          title={collapsed ? displayName : undefined}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-md border border-transparent text-left transition-colors",
            "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
            collapsed ? "justify-center p-1.5" : "px-2.5 py-2",
            open ? "bg-accent border-border" : "hover:bg-accent",
          )}
        >
          <span
            aria-hidden
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground"
          >
            {initials}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-semibold">
                  {displayName}
                </span>
                {user.email && (
                  <span className="block truncate text-[10px] text-muted-foreground">
                    {user.email}
                  </span>
                )}
              </span>
              <HugeiconsIcon
                icon={MoreHorizontalIcon}
                size={14}
                className="shrink-0 text-muted-foreground"
              />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        sideOffset={6}
        className="w-64 p-1.5"
      >
        <div className="px-2.5 pb-1.5 pt-1">
          <div className="text-xs font-semibold">{displayName}</div>
          {user.email && (
            <div className="text-[10px] text-muted-foreground">
              {user.email}
            </div>
          )}
        </div>

        <div className="my-1 border-t border-border" />

        <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Theme
        </div>
        <div className="flex gap-1 px-1.5 pb-1.5">
          <button
            type="button"
            onClick={() => handleThemeChange("light")}
            aria-pressed={currentTheme === "light"}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition-colors",
              currentTheme === "light"
                ? "border-border bg-accent text-foreground"
                : "border-transparent text-muted-foreground hover:bg-accent",
            )}
          >
            <HugeiconsIcon icon={Sun03Icon} size={14} />
            Light
          </button>
          <button
            type="button"
            onClick={() => handleThemeChange("system")}
            aria-pressed={currentTheme === "system"}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition-colors",
              currentTheme === "system"
                ? "border-border bg-accent text-foreground"
                : "border-transparent text-muted-foreground hover:bg-accent",
            )}
          >
            <HugeiconsIcon icon={ComputerIcon} size={14} />
            System
          </button>
          <button
            type="button"
            onClick={() => handleThemeChange("dark")}
            aria-pressed={currentTheme === "dark"}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition-colors",
              currentTheme === "dark"
                ? "border-border bg-accent text-foreground"
                : "border-transparent text-muted-foreground hover:bg-accent",
            )}
          >
            <HugeiconsIcon icon={Moon02Icon} size={14} />
            Dark
          </button>
        </div>

        <div className="my-1 border-t border-border" />

        <div className="px-2.5 pb-1 pt-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Language
          </span>
        </div>
        <div className="relative px-1.5 pb-1">
          <HugeiconsIcon
            icon={Search01Icon}
            size={12}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search languages…"
            aria-label="Search languages"
            className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none focus:ring-[3px] focus:ring-ring/40"
          />
        </div>
        <div className="flex max-h-48 flex-col gap-px overflow-y-auto px-1.5 pb-1">
          {filteredLangs.length === 0 ? (
            <div className="px-2.5 py-3 text-center text-[11px] text-muted-foreground">
              No languages match.
            </div>
          ) : (
            filteredLangs.map((l) => {
              const active = language === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => handleSelectLanguage(l.code)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-xs text-foreground transition-colors",
                    active ? "bg-accent" : "hover:bg-accent",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-left">
                    {l.flag ? `${l.flag} ` : ""}
                    {l.name}
                  </span>
                  {active && <HugeiconsIcon icon={Tick02Icon} size={12} />}
                </button>
              );
            })
          )}
        </div>

        <div className="my-1 border-t border-border" />

        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-xs text-danger transition-colors hover:bg-danger-bg"
        >
          <HugeiconsIcon icon={LogoutSquare01Icon} size={14} />
          <span>Sign out</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}
